import {
  ExportMonitoringEntriesQueryControllerExportCheckSlug,
  ExportMonitoringEntriesQueryControllerExportScope,
  ExportMonitoringEntriesQueryControllerExportSortBy,
  ExportMonitoringEntriesQueryControllerExportSortDir,
  ExportMonitoringEntriesQueryControllerExportStatus,
} from "@/generated/model";
import { exportMonitoringEntriesQueryControllerExport } from "@/generated/monitoring/monitoring";
import { getUkTodayStr } from "@/lib/date-utils";
import { downloadBlob } from "@/lib/download";
import { enumValue } from "@/lib/enum-value";

/**
 * The filters as the pages hold them — plain strings from search params and
 * state — narrowed to the generated types in one place, as use-monitoring-data
 * does for the list.
 */
export interface MonitoringExportParams {
  scope?: string;
  status?: string;
  logTypeId?: string;
  checkSlug?: string;
  locationId?: string;
  from?: string;
  to?: string;
  sortBy?: string;
  sortDir?: string;
}

/** Pulls the server's filename out of Content-Disposition, falling back sensibly. */
function filenameFrom(header: unknown, fallback: string): string {
  if (typeof header !== "string") return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // A malformed header should not stop the download.
    }
  }
  return /filename="([^"]+)"/i.exec(header)?.[1] ?? fallback;
}

/** Downloads the monitoring CSV for the filters currently on screen (NAU-411 AC14/AC24). */
export async function downloadMonitoringCsv(
  nurseryId: string,
  params: MonitoringExportParams,
): Promise<void> {
  const response = await exportMonitoringEntriesQueryControllerExport(
    nurseryId,
    {
      scope: enumValue(
        ExportMonitoringEntriesQueryControllerExportScope,
        params.scope,
      ),
      status: enumValue(
        ExportMonitoringEntriesQueryControllerExportStatus,
        params.status,
      ),
      logTypeId: params.logTypeId,
      checkSlug: enumValue(
        ExportMonitoringEntriesQueryControllerExportCheckSlug,
        params.checkSlug,
      ),
      locationId: params.locationId,
      from: params.from,
      to: params.to,
      sortBy: enumValue(
        ExportMonitoringEntriesQueryControllerExportSortBy,
        params.sortBy,
      ),
      sortDir: enumValue(
        ExportMonitoringEntriesQueryControllerExportSortDir,
        params.sortDir,
      ),
    },
  );

  const fallback = `monitoring-${getUkTodayStr()}.csv`;
  const header = (response as { headers?: Record<string, unknown> }).headers?.[
    "content-disposition"
  ];
  downloadBlob(response.data, filenameFrom(header, fallback));
}

/**
 * The server's reason for a failed export, e.g. "narrow the date range". The
 * request asks for a Blob, so the JSON error body arrives as a Blob too and has
 * to be read before it can be shown.
 */
export async function exportErrorMessage(
  error: unknown,
): Promise<string | undefined> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return undefined;
  try {
    const body = JSON.parse(await data.text()) as { message?: unknown };
    return typeof body.message === "string" ? body.message : undefined;
  } catch {
    return undefined;
  }
}
